import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileSignature, Loader2, Copy, CheckCircle2, Scale } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TONES = [
    { value: "firm", label: "Firm (recommended)" },
    { value: "neutral", label: "Neutral / professional" },
    { value: "aggressive", label: "Aggressive" },
];

export default function DemandLetter() {
    const [form, setForm] = useState({
        sender_name: "",
        sender_address: "",
        recipient_name: "",
        recipient_address: "",
        incident_date: "",
        state: "",
        facts: "",
        damages_amount: "",
        damages_description: "",
        deadline_days: 30,
        tone: "firm",
    });
    const [busy, setBusy] = useState(false);
    const [letter, setLetter] = useState("");
    const [disclaimer, setDisclaimer] = useState("");
    const [copied, setCopied] = useState(false);

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!form.sender_name.trim()) return toast.error("Enter the sender's name");
        if (!form.recipient_name.trim()) return toast.error("Enter the recipient's name");
        if (form.facts.trim().length < 30) return toast.error("Describe the facts (at least 30 characters)");
        setBusy(true);
        setLetter("");
        try {
            const payload = {
                ...form,
                damages_amount: form.damages_amount ? Number(form.damages_amount) : null,
                deadline_days: Number(form.deadline_days) || 30,
            };
            const { data } = await axios.post(`${API}/tools/demand-letter`, payload);
            setLetter(data.letter || "");
            setDisclaimer(data.disclaimer || "");
            toast.success("Demand letter ready");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Generation failed. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(letter);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast.success("Copied to clipboard");
        } catch (e) {
            toast.error("Copy failed");
        }
    };

    const downloadTxt = () => {
        const blob = new Blob([letter], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `demand-letter-${(form.sender_name || "draft").replace(/\s+/g, "_").toLowerCase()}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-black text-white" data-testid="demand-letter-page">
            <Header />
            <main className="max-w-[1320px] mx-auto px-6 sm:px-10 py-14">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30] mb-3 flex items-center gap-2">
                    <Scale size={11} /> HPM TOOLS · PRE-LITIGATION
                </div>
                <h1 className="font-anton text-5xl sm:text-6xl uppercase leading-[0.95] mb-4">
                    Demand letter, drafted in 30 seconds.
                </h1>
                <p className="font-sans text-base sm:text-lg text-white/75 leading-relaxed max-w-3xl mb-10">
                    Tell us what happened, who&apos;s liable, and what you&apos;re owed. Our
                    Claude-powered drafter produces a clean, formal demand letter you can
                    review with counsel and send. Free. No card.
                </p>

                <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-10">
                    <form
                        onSubmit={submit}
                        className="border-2 border-white/20 p-6 sm:p-8 space-y-5"
                        data-testid="demand-letter-form"
                    >
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Your full name" required>
                                <input value={form.sender_name} onChange={(e) => update("sender_name", e.target.value)} data-testid="demand-sender-name" className={INPUT_CLS} />
                            </Field>
                            <Field label="Your address (optional)">
                                <input value={form.sender_address} onChange={(e) => update("sender_address", e.target.value)} data-testid="demand-sender-address" className={INPUT_CLS} />
                            </Field>
                            <Field label="Recipient name" required>
                                <input value={form.recipient_name} onChange={(e) => update("recipient_name", e.target.value)} data-testid="demand-recipient-name" className={INPUT_CLS} />
                            </Field>
                            <Field label="Recipient address (optional)">
                                <input value={form.recipient_address} onChange={(e) => update("recipient_address", e.target.value)} data-testid="demand-recipient-address" className={INPUT_CLS} />
                            </Field>
                            <Field label="State (US)">
                                <input value={form.state} onChange={(e) => update("state", e.target.value)} placeholder="Pennsylvania" data-testid="demand-state" className={INPUT_CLS} />
                            </Field>
                            <Field label="Incident date">
                                <input type="date" value={form.incident_date} onChange={(e) => update("incident_date", e.target.value)} data-testid="demand-incident-date" className={INPUT_CLS} />
                            </Field>
                        </div>

                        <Field label="What happened? (facts)" required>
                            <textarea
                                value={form.facts}
                                onChange={(e) => update("facts", e.target.value)}
                                rows={6}
                                placeholder="On May 3, 2025, I was performing contracted services for ACME Co. When I delivered the work product, ACME refused to pay the agreed $4,200 invoice despite multiple written reminders…"
                                data-testid="demand-facts"
                                className={`${INPUT_CLS} font-sans`}
                            />
                        </Field>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <Field label="Amount demanded (USD)">
                                <input type="number" min="0" step="0.01" value={form.damages_amount} onChange={(e) => update("damages_amount", e.target.value)} placeholder="4200" data-testid="demand-amount" className={INPUT_CLS} />
                            </Field>
                            <Field label="Response deadline (days)">
                                <input type="number" min="7" max="120" value={form.deadline_days} onChange={(e) => update("deadline_days", e.target.value)} data-testid="demand-deadline" className={INPUT_CLS} />
                            </Field>
                        </div>

                        <Field label="What damages? (brief description)">
                            <input value={form.damages_description} onChange={(e) => update("damages_description", e.target.value)} placeholder="Unpaid invoice + interest + collection costs" data-testid="demand-damages-desc" className={INPUT_CLS} />
                        </Field>

                        <Field label="Tone">
                            <select value={form.tone} onChange={(e) => update("tone", e.target.value)} data-testid="demand-tone" className={INPUT_CLS}>
                                {TONES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </Field>

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="demand-submit"
                            className="w-full bg-[#FF3B30] text-white px-6 py-3 font-anton text-xl uppercase tracking-wider hover:bg-white hover:text-black transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                        >
                            {busy ? <Loader2 size={18} className="animate-spin" /> : <FileSignature size={18} />}
                            {busy ? "Drafting…" : "Generate demand letter"}
                        </button>

                        <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 leading-relaxed">
                            INFORMATIONAL ONLY — NOT LEGAL ADVICE. REVIEW WITH A LICENSED ATTORNEY BEFORE SENDING.
                        </p>
                    </form>

                    <div className="border-2 border-white/20 p-6 sm:p-8 flex flex-col" data-testid="demand-letter-result">
                        {!letter ? (
                            <div className="flex-1 flex flex-col items-start justify-center gap-4 text-white/40 font-mono text-[11px] uppercase tracking-[0.25em]">
                                <FileSignature size={32} className="text-white/30" />
                                FILL THE FORM ·<br />YOUR DRAFT APPEARS HERE
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF3B30]">
                                        DRAFT — REVIEW BEFORE SENDING
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={copyToClipboard}
                                            data-testid="demand-copy-btn"
                                            className="border-2 border-white/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-white hover:text-black flex items-center gap-2"
                                        >
                                            {copied ? <CheckCircle2 size={11} /> : <Copy size={11} />}
                                            {copied ? "COPIED" : "COPY"}
                                        </button>
                                        <button
                                            onClick={downloadTxt}
                                            data-testid="demand-download-btn"
                                            className="border-2 border-white/40 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] hover:bg-white hover:text-black"
                                        >
                                            .TXT
                                        </button>
                                    </div>
                                </div>
                                <pre
                                    className="whitespace-pre-wrap font-sans text-[14px] leading-relaxed text-white/90 bg-white/5 p-5 border border-white/10 flex-1 overflow-auto"
                                    data-testid="demand-letter-text"
                                >
{letter}
                                </pre>
                                {disclaimer && (
                                    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 leading-relaxed mt-4">
                                        {disclaimer}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
        </div>
    );
}

const INPUT_CLS =
    "w-full bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm focus:border-[#FF3B30] outline-none";

function Field({ label, required, children }) {
    return (
        <label className="block">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60 mb-2">
                {label}{required && <span className="text-[#FF3B30]"> *</span>}
            </div>
            {children}
        </label>
    );
}
